import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  addOperationalExpense,
  cancelOperationalExpense,
  fetchOperationalExpenseDetail,
  fetchOperationalExpenses,
  updateOperationalExpense,
} from "../../actions/operationalExpenseAction";
import { AlertConfirmModal } from "../../components/Modals/AlertConfirmModal";
import AkasSelect from "../../components/AkasSelect";
import { formatIDR } from "../../utils/helpers/formatIDR";
import { hasAnyPermission } from "../../utils/helpers/permissions";
import "../CashBankPage/cash-bank.css";
import DetailModal from "./components/DetailModal";
import FormModal from "./components/FormModal";
import { PERIOD_LABELS, STATUS_META, formatDateTime } from "./constants";
import "./operational-expense.css";

const localDate = (date) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};
const initialFilters = () => {
  const now = new Date();
  return {
    start_date: localDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    end_date: localDate(now),
    category_id: "",
    period_type: "",
    status: "",
    search: "",
  };
};

const ExpenseGlyph = ({ type = "expense" }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {type === "category" ? <><path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4z" /><circle cx="17" cy="17" r="3" /></> : type === "count" ? <><path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16l-3-2-2 2-2-2-2 2-3-2z" /><path d="M9 8h6M9 12h6" /></> : <><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M7 10h10M7 15h4M17 15h.01" /></>}
  </svg>
);

const ActionIcon = ({ type }) => type === "detail" ? (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="icon" aria-hidden="true"><path d="M13 5h8M13 9h5M13 15h8M13 19h5" /><path d="M3 5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5M3 15a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4" /></svg>
) : type === "edit" ? (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="icon" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1l1-4z" /></svg>
) : (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="icon" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
);

export const OperationalExpensePage = (props) => {
  const {
    data,
    loading,
    actionLoading,
    error,
    userProfile,
    fetchOperationalExpenses,
    fetchOperationalExpenseDetail,
    addOperationalExpense,
    updateOperationalExpense,
    cancelOperationalExpense,
  } = props;
  const [filters, setFilters] = useState(initialFilters);
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [formExpense, setFormExpense] = useState(undefined);
  const [detailExpense, setDetailExpense] = useState(null);
  const [pendingCancel, setPendingCancel] = useState(null);

  const categories = useMemo(() => data?.categories || [], [data]);
  const accounts = useMemo(() => data?.accounts || [], [data]);
  const pagination = data?.expenses || {};
  const expenses = pagination?.data || [];
  const summary = data?.summary || {};
  const canCreate = hasAnyPermission(userProfile, ["create-operational-expenses"]);
  const canEdit = hasAnyPermission(userProfile, ["edit-operational-expenses"]);
  const canCancel = hasAnyPermission(userProfile, ["delete-operational-expenses"]);

  const load = useCallback(() => fetchOperationalExpenses({
    ...filters,
    category_id: filters.category_id || undefined,
    period_type: filters.period_type || undefined,
    status: filters.status || undefined,
    search: filters.search || undefined,
    page: currentPage,
    limit: perPage,
  }), [currentPage, fetchOperationalExpenses, filters, perPage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setCurrentPage(1);
      setFilters((current) => ({ ...current, search: searchInput.trim() }));
    }, 320);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const updateFilter = (key, value) => {
    setCurrentPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };
  const saveExpense = async (payload) => {
    const result = formExpense?.id
      ? await updateOperationalExpense(formExpense.id, payload)
      : await addOperationalExpense(payload);
    if (result) {
      setFormExpense(undefined);
      await load();
    }
  };
  const openDetail = async (expense) => {
    const result = await fetchOperationalExpenseDetail(expense.id);
    if (result) setDetailExpense(result);
  };
  const confirmCancel = async (_id, reason) => {
    if (!pendingCancel) return false;
    const result = await cancelOperationalExpense(pendingCancel.id, reason);
    if (!result) return false;
    setPendingCancel(null);
    if (detailExpense?.id === result.id) setDetailExpense(result);
    await load();
    return true;
  };

  return (
    <div className="page-wrapper cash-bank-page operational-expense-page">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row align-items-center g-3">
            <div className="col">
              <div className="page-pretitle">KONTROL BEBAN USAHA</div>
              <h1 className="page-title">Biaya Operasional</h1>
              <p className="text-secondary mb-0">Catat biaya non-stok agar laba bersih operasional dan saldo usaha selalu akurat.</p>
            </div>
            <div className="col-auto">
              {canCreate ? <button type="button" className="btn btn-primary" onClick={() => setFormExpense(null)}>+ Tambah biaya</button> : null}
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <section className="operational-expense-filter-card" aria-label="Filter biaya operasional">
            <div className="operational-expense-filter-copy"><strong>Periode pencatatan</strong><span>Ringkasan memakai ledger biaya, termasuk reversal pada tanggal pembatalan.</span></div>
            <div className="operational-expense-filter-grid">
              <label><span>Dari</span><input type="date" className="form-control" value={filters.start_date} max={filters.end_date} onChange={(event) => updateFilter("start_date", event.target.value)} /></label>
              <label><span>Sampai</span><input type="date" className="form-control" value={filters.end_date} min={filters.start_date} onChange={(event) => updateFilter("end_date", event.target.value)} /></label>
              <label><span>Kategori</span><AkasSelect className="form-select" value={filters.category_id} onChange={(event) => updateFilter("category_id", event.target.value)}><option value="">Semua kategori</option>{categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</AkasSelect></label>
              <label><span>Periode biaya</span><AkasSelect className="form-select" value={filters.period_type} onChange={(event) => updateFilter("period_type", event.target.value)}><option value="">Semua periode</option>{Object.entries(PERIOD_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</AkasSelect></label>
              <label><span>Status</span><AkasSelect className="form-select" value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}><option value="">Semua status</option><option value="paid">Tercatat</option><option value="cancelled">Dibatalkan</option></AkasSelect></label>
              <label className="operational-expense-search"><span>Pencarian</span><div className="input-icon"><span className="input-icon-addon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg></span><input type="search" className="form-control" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Nomor, kategori, akun..." /></div></label>
            </div>
          </section>

          {error ? <div className="alert alert-danger">{error}</div> : null}

          <section className="cash-bank-summary-grid operational-expense-summary-grid" aria-label="Ringkasan biaya operasional">
            <article className="cash-bank-summary-card is-primary akas-summary-card">
              <span className="cash-bank-summary-icon"><ExpenseGlyph /></span>
              <div><small>Biaya bersih</small><strong>{loading && !data ? "..." : formatIDR(summary.net_amount)}</strong><p>Biaya tercatat dikurangi reversal periode ini</p></div>
            </article>
            <article className="cash-bank-summary-card akas-summary-card">
              <span className="cash-bank-summary-icon"><ExpenseGlyph type="count" /></span>
              <div><small>Transaksi biaya</small><strong>{Number(summary.posted_count || 0).toLocaleString("id-ID")}</strong><p>Rata-rata {formatIDR(summary.average_amount)}</p></div>
            </article>
            <article className="cash-bank-summary-card akas-summary-card">
              <span className="cash-bank-summary-icon"><ExpenseGlyph type="category" /></span>
              <div><small>Kategori terbesar</small><strong className="operational-expense-top-category">{summary.top_category?.name || "Belum ada"}</strong><p>{summary.top_category ? formatIDR(summary.top_category.total) : "Belum ada transaksi"}</p></div>
            </article>
          </section>

          <section className="card operational-expense-table-card">
            <div className="card-header">
              <div><h3 className="card-title mb-1">Daftar biaya</h3><p className="text-secondary mb-0">Transaksi non-stok yang memengaruhi laba bersih operasional.</p></div>
              <span className="operational-expense-record-count">{pagination.total || 0} data</span>
            </div>
            <div className="table-responsive">
              <table className="table table-vcenter card-table operational-expense-table">
                <thead><tr><th>No.</th><th>Biaya</th><th>Kategori</th><th>Waktu</th><th>Periode</th><th>Akun</th><th className="text-end">Nominal</th><th>Status</th><th className="text-end">Aksi</th></tr></thead>
                <tbody>
                  {loading && !expenses.length ? Array.from({ length: 5 }).map((_, index) => <tr key={`expense-skeleton-${index}`} className="placeholder-glow"><td><span className="placeholder col-7" /></td><td><span className="placeholder col-9" /></td><td><span className="placeholder col-8" /></td><td><span className="placeholder col-8" /></td><td><span className="placeholder col-7" /></td><td><span className="placeholder col-8" /></td><td><span className="placeholder col-9" /></td><td><span className="placeholder col-8" /></td><td><span className="placeholder col-8" /></td></tr>) : null}
                  {!loading && !expenses.length ? <tr><td colSpan="9" className="text-center py-5"><div className="operational-expense-empty"><span><ExpenseGlyph /></span><strong>Belum ada biaya operasional</strong><p>Catat listrik, gaji, sewa, internet, dan kebutuhan non-stok lainnya.</p>{canCreate ? <button type="button" className="btn btn-primary btn-sm" onClick={() => setFormExpense(null)}>Tambah biaya pertama</button> : null}</div></td></tr> : null}
                  {expenses.map((expense, index) => {
                    const status = STATUS_META[expense.status] || { label: expense.status, className: "bg-secondary-lt" };
                    return (
                      <tr key={expense.id} className={expense.status === "cancelled" ? "is-cancelled" : ""}>
                        <td className="text-secondary">{Number(pagination.from || 1) + index}</td>
                        <td><button type="button" className="operational-expense-name" onClick={() => openDetail(expense)}><strong>{expense.expense_no}</strong><small>{expense.note || "Tanpa keterangan"}</small></button></td>
                        <td><span className="operational-expense-category"><i />{expense.category_name}</span></td>
                        <td><strong className="operational-expense-date">{formatDateTime(expense.occurred_at)}</strong><small className="d-block text-secondary">{expense.created_by_name || "Sistem"}</small></td>
                        <td>
                          <span className="badge bg-secondary-lt">{PERIOD_LABELS[expense.period_type] || expense.period_type}</span>
                          {expense.schedule_id ? (
                            <span className={`operational-expense-recurring-status ${expense.schedule_is_active ? "" : "is-paused"}`}>
                              {expense.schedule_is_active ? "Otomatis aktif" : "Otomatis nonaktif"}
                            </span>
                          ) : null}
                          {expense.schedule_last_error ? <small className="operational-expense-schedule-error">Jadwal tertunda: {expense.schedule_last_error}</small> : null}
                        </td>
                        <td><strong>{expense.account_name}</strong><small className="d-block text-secondary">{expense.account_type === "cash" ? "Kas tunai" : expense.account_type === "bank" ? "Rekening bank" : "Dompet digital"}</small></td>
                        <td className="text-end"><strong className="operational-expense-amount">{formatIDR(expense.amount)}</strong></td>
                        <td><span className={`badge ${status.className}`}>{status.label}</span></td>
                        <td className="text-end">
                          <div className="btn-list flex-nowrap justify-content-end">
                            <button type="button" className="btn btn-1 bg-secondary-lt" onClick={() => openDetail(expense)} aria-label={`Lihat detail ${expense.expense_no}`}>
                              <ActionIcon type="detail" />
                              Detail
                            </button>
                            {canEdit && expense.status !== "cancelled" ? (
                              <button type="button" className="btn btn-1 bg-primary-lt" onClick={() => setFormExpense(expense)} aria-label={`Edit ${expense.expense_no}`}>
                                <ActionIcon type="edit" />
                                Edit
                              </button>
                            ) : null}
                            {canCancel && expense.status !== "cancelled" ? (
                              <button type="button" className="btn btn-1 bg-danger-lt" data-bs-toggle="modal" data-bs-target="#modal-cancel-operational-expense" onClick={() => setPendingCancel(expense)} aria-label={`Batalkan ${expense.expense_no}`}>
                                <ActionIcon type="cancel" />
                                Batalkan
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="card-footer operational-expense-pagination">
              <div className="dropdown"><button type="button" className="btn dropdown-toggle" data-bs-toggle="dropdown">{perPage} data</button><div className="dropdown-menu">{[10, 20, 50, 100].map((value) => <button key={value} type="button" className="dropdown-item" onClick={() => { setPerPage(value); setCurrentPage(1); }}>{value} data</button>)}</div></div>
              <span className="text-secondary">Menampilkan {pagination.from || 0}–{pagination.to || 0} dari {pagination.total || 0}</span>
              <ul className="pagination m-0 ms-auto">
                <li className={`page-item ${currentPage <= 1 ? "disabled" : ""}`}><button type="button" className="page-link" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} aria-label="Halaman sebelumnya">‹</button></li>
                {(pagination.links || []).slice(1, -1).map((item, index) => { const page = item.url ? Number(new URL(item.url).searchParams.get("page")) : null; return <li key={`${item.label}-${index}`} className={`page-item ${item.active ? "active" : ""}`}><button type="button" className="page-link" disabled={!page} onClick={() => page && setCurrentPage(page)}>{item.label}</button></li>; })}
                <li className={`page-item ${currentPage >= Number(pagination.last_page || 1) ? "disabled" : ""}`}><button type="button" className="page-link" onClick={() => setCurrentPage((page) => Math.min(Number(pagination.last_page || 1), page + 1))} aria-label="Halaman berikutnya">›</button></li>
              </ul>
            </div>
          </section>
        </div>
      </div>

      {formExpense !== undefined ? <FormModal key={formExpense?.id || "create"} expense={formExpense} categories={categories} accounts={accounts} saving={actionLoading} onClose={() => setFormExpense(undefined)} onSave={saveExpense} /> : null}
      {detailExpense ? <DetailModal expense={detailExpense} onClose={() => setDetailExpense(null)} /> : null}
      <AlertConfirmModal
        type="error"
        title="Batalkan biaya operasional?"
        message={pendingCancel ? `${pendingCancel.expense_no} · ${pendingCancel.category_name} sebesar ${formatIDR(pendingCancel.amount)}. Saldo akan dikembalikan ke ${pendingCancel.account_name}.` : "Saldo Kas & Bank akan dikembalikan melalui reversal."}
        confirmText="Batalkan biaya"
        cancelText="Kembali"
        requireReason
        reasonLabel="Alasan pembatalan"
        reasonPlaceholder="Contoh: transaksi ganda atau akun pembayaran salah..."
        reasonHint="Alasan disimpan bersama jejak reversal dan tidak dapat dihapus."
        waitForConfirm
        idModal="cancel-operational-expense"
        idData={pendingCancel?.id}
        onConfirm={confirmCancel}
        onCancel={() => setPendingCancel(null)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  data: state.operationalExpenses?.data,
  detail: state.operationalExpenses?.detail,
  loading: state.operationalExpenses?.loading,
  actionLoading: state.operationalExpenses?.actionLoading,
  error: state.operationalExpenses?.error,
  userProfile: state.auth?.login?.profile,
});

export default connect(mapStateToProps, {
  fetchOperationalExpenses,
  fetchOperationalExpenseDetail,
  addOperationalExpense,
  updateOperationalExpense,
  cancelOperationalExpense,
})(OperationalExpensePage);
